// src/pages/languages/LanguagesPage.js
// Modern, responsive redesign matching the rest of the cinematic landing (pure black,
// typography-forward, minimal chrome). Framer-motion entrance via IntersectionObserver.
import React, { useRef } from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import { motion, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import useSEO from '../../hooks/useSEO';
import Breadcrumbs from '../../components/common/Breadcrumbs';

const BREADCRUMBS = [{ name: 'Languages', url: 'https://dublabs.ai/languages' }];

// ─── Data ─────────────────────────────────────────────────────────────────
// Canonical list of the 32 languages we actually ship today.
// Featured = major-market wedges; the rest live in the "All Supported" grid.
const FEATURED_LANGUAGES = [
  { name: 'English',    native: 'English',   flag: '🇺🇸', speakers: '1.5B+', growth: '+120%', desc: 'The default global language — English opens every major market on the web.' },
  { name: 'Chinese',    native: '中文',       flag: '🇨🇳', speakers: '918M+', growth: '+150%', desc: 'Connect with the world\'s largest language audience across Mainland China and beyond.' },
  { name: 'Hindi',      native: 'हिन्दी',    flag: '🇮🇳', speakers: '600M+', growth: '+200%', desc: 'Reach the massive Hindi-speaking audience across India and the diaspora.' },
  { name: 'Spanish',    native: 'Español',   flag: '🇪🇸', speakers: '500M+', growth: '+130%', desc: 'Reach the massive Spanish-speaking market across 21 countries.' },
  { name: 'Arabic',     native: 'العربية',   flag: '🇸🇦', speakers: '422M+', growth: '+95%',  desc: 'Reach Arabic-speaking audiences across MENA and the diaspora.' },
  { name: 'Bengali',    native: 'বাংলা',    flag: '🇮🇳', speakers: '265M+', growth: '+160%', desc: 'Reach Bengali-speaking audiences across India, Bangladesh, and the global diaspora.' },
  { name: 'Portuguese', native: 'Português', flag: '🇵🇹', speakers: '260M+', growth: '+90%',  desc: 'Cover Brazil, Portugal, and Lusophone Africa with authentic voice.' },
  { name: 'Russian',    native: 'Русский',   flag: '🇷🇺', speakers: '258M+', growth: '+80%',  desc: 'Connect with Russian-speaking audiences across Eastern Europe and Central Asia.' },
  { name: 'Japanese',   native: '日本語',      flag: '🇯🇵', speakers: '125M+', growth: '+90%',  desc: 'Reach Japanese audiences with perfect pronunciation and prosody.' },
  { name: 'Marathi',    native: 'मराठी',    flag: '🇮🇳', speakers: '99M+',  growth: '+150%', desc: 'Reach Marathi-speaking audiences across Maharashtra and the wider Indian diaspora.' },
  { name: 'Telugu',     native: 'తెలుగు',   flag: '🇮🇳', speakers: '96M+',  growth: '+145%', desc: 'Connect with Telugu speakers across Andhra Pradesh, Telangana, and beyond.' },
  { name: 'French',     native: 'Français',  flag: '🇫🇷', speakers: '280M+', growth: '+85%',  desc: 'Connect with French speakers in France, Canada, and Africa.' },
];

const ALL_LANGUAGES = [
  { name: 'German',     native: 'Deutsch',    flag: '🇩🇪', speakers: '100M+' },
  { name: 'Turkish',    native: 'Türkçe',     flag: '🇹🇷', speakers: '80M+'  },
  { name: 'Korean',     native: '한국어',        flag: '🇰🇷', speakers: '77M+'  },
  { name: 'Italian',    native: 'Italiano',   flag: '🇮🇹', speakers: '65M+'  },
  { name: 'Polish',     native: 'Polski',     flag: '🇵🇱', speakers: '45M+'  },
  { name: 'Filipino',   native: 'Filipino',   flag: '🇵🇭', speakers: '45M+'  },
  { name: 'Indonesian', native: 'Indonesia',  flag: '🇮🇩', speakers: '43M+'  },
  { name: 'Ukrainian',  native: 'Українська', flag: '🇺🇦', speakers: '41M+'  },
  { name: 'Tamil',      native: 'தமிழ்',      flag: '🇮🇳', speakers: '86M+'  },
  { name: 'Romanian',   native: 'Română',     flag: '🇷🇴', speakers: '24M+'  },
  { name: 'Dutch',      native: 'Nederlands', flag: '🇳🇱', speakers: '24M+'  },
  { name: 'Malay',      native: 'Melayu',     flag: '🇲🇾', speakers: '19M+'  },
  { name: 'Greek',      native: 'Ελληνικά',   flag: '🇬🇷', speakers: '13M+'  },
  { name: 'Czech',      native: 'Čeština',    flag: '🇨🇿', speakers: '10M+'  },
  { name: 'Swedish',    native: 'Svenska',    flag: '🇸🇪', speakers: '10M+'  },
  { name: 'Bulgarian',  native: 'Български',  flag: '🇧🇬', speakers: '8M+'   },
  { name: 'Danish',     native: 'Dansk',      flag: '🇩🇰', speakers: '6M+'   },
  { name: 'Croatian',   native: 'Hrvatski',   flag: '🇭🇷', speakers: '5M+'   },
  { name: 'Slovak',     native: 'Slovenčina', flag: '🇸🇰', speakers: '5M+'   },
  { name: 'Finnish',    native: 'Suomi',      flag: '🇫🇮', speakers: '5M+'   },
];

// ─── Section-heading helper ───────────────────────────────────────────────
function SectionHeading({ eyebrow, title, subtitle }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.5, once: true });
  return (
    <Box ref={ref} sx={{ textAlign: 'center', mb: { xs: 6, md: 8 }, maxWidth: 780, mx: 'auto', px: 2 }}>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      >
        {eyebrow && (
          <Typography sx={{
            fontFamily: 'Inter', fontWeight: 500, fontSize: '0.68rem',
            letterSpacing: '0.24em', textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.35)', mb: 2.5,
          }}>
            {eyebrow}
          </Typography>
        )}
        <Typography component="h2" sx={{
          fontFamily: 'Inter', fontWeight: 900,
          fontSize: 'clamp(1.9rem, 4.5vw, 3.6rem)',
          color: '#fff', lineHeight: 1.05, letterSpacing: '-0.04em', mb: subtitle ? 2 : 0,
        }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{
            fontFamily: 'Inter', fontWeight: 300,
            fontSize: 'clamp(0.9rem, 1.4vw, 1.05rem)',
            color: 'rgba(255,255,255,0.45)', lineHeight: 1.6,
            maxWidth: 600, mx: 'auto',
          }}>
            {subtitle}
          </Typography>
        )}
      </motion.div>
    </Box>
  );
}

// ─── Featured language card ───────────────────────────────────────────────
function FeaturedCard({ lang, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.3, once: true });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: (index % 3) * 0.08 }}
      style={{ height: '100%' }}
    >
      <Box sx={{
        height: '100%',
        p: { xs: 3, md: 3.5 },
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 2,
        background: 'rgba(255,255,255,0.015)',
        backdropFilter: 'blur(12px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        transition: 'border-color 0.35s ease, background 0.35s ease, transform 0.35s ease',
        '&:hover': {
          borderColor: 'rgba(255,255,255,0.28)',
          background: 'rgba(255,255,255,0.03)',
          transform: 'translateY(-2px)',
        },
      }}>
        {/* Top row: flag + native script */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ fontSize: 34, lineHeight: 1 }}>{lang.flag}</Box>
          <Typography sx={{
            fontFamily: 'Inter', fontWeight: 400,
            fontSize: '0.85rem',
            color: 'rgba(255,255,255,0.35)',
            letterSpacing: '0.02em',
          }}>
            {lang.native}
          </Typography>
        </Box>

        <Typography sx={{
          fontFamily: 'Inter', fontWeight: 700,
          fontSize: '1.5rem', color: '#fff',
          letterSpacing: '-0.02em', lineHeight: 1.1,
        }}>
          {lang.name}
        </Typography>

        <Typography sx={{
          fontFamily: 'Inter', fontWeight: 300,
          fontSize: '0.85rem',
          color: 'rgba(255,255,255,0.5)',
          lineHeight: 1.55,
          flexGrow: 1,
        }}>
          {lang.desc}
        </Typography>

        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 2,
          pt: 1.5, mt: 1,
          borderTop: '1px solid rgba(255,255,255,0.06)',
        }}>
          <Box>
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 500, fontSize: '0.6rem',
              letterSpacing: '0.18em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.35)', mb: 0.3,
            }}>
              Speakers
            </Typography>
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 600, fontSize: '0.95rem',
              color: '#fff', letterSpacing: '-0.01em',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {lang.speakers}
            </Typography>
          </Box>
          <Box sx={{ ml: 'auto', textAlign: 'right' }}>
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 500, fontSize: '0.6rem',
              letterSpacing: '0.18em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.35)', mb: 0.3,
            }}>
              Growth
            </Typography>
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 600, fontSize: '0.95rem',
              color: '#fff', letterSpacing: '-0.01em',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {lang.growth}
            </Typography>
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
}

// ─── Compact language chip (for All Supported) ────────────────────────────
function LanguageChip({ lang, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.4, once: true });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 10 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], delay: (index % 6) * 0.04 }}
    >
      <Box sx={{
        p: { xs: 2, md: 2.25 },
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: 1.5,
        background: 'rgba(255,255,255,0.01)',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        textAlign: 'center', gap: 0.75, minHeight: 118,
        transition: 'border-color 0.3s ease, background 0.3s ease',
        '&:hover': {
          borderColor: 'rgba(255,255,255,0.22)',
          background: 'rgba(255,255,255,0.025)',
        },
      }}>
        <Box sx={{ fontSize: 24, lineHeight: 1 }}>{lang.flag}</Box>
        <Typography sx={{
          fontFamily: 'Inter', fontWeight: 600,
          fontSize: '0.85rem', color: '#fff',
          letterSpacing: '-0.005em',
        }}>
          {lang.name}
        </Typography>
        {lang.native && (
          <Typography sx={{
            fontFamily: 'Inter', fontWeight: 400,
            fontSize: '0.7rem',
            color: 'rgba(255,255,255,0.35)',
          }}>
            {lang.native}
          </Typography>
        )}
        <Typography sx={{
          fontFamily: 'Inter', fontWeight: 500, fontSize: '0.62rem',
          letterSpacing: '0.1em', textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.4)',
          fontVariantNumeric: 'tabular-nums',
        }}>
          {lang.speakers}
        </Typography>
      </Box>
    </motion.div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────
export default function LanguagesPage() {
  const navigate = useNavigate();

  useSEO({
    title: '32 Languages Supported by DubLabs AI Video Dubbing',
    description:
      'Professional AI video dubbing in 32 languages including English, Hindi, Chinese, Spanish, Arabic, Tamil, and more. Perfect lip-sync + AI voice cloning.',
    canonical: 'https://dublabs.ai/languages',
    breadcrumbs: BREADCRUMBS,
    dateModified: '2026-07-30',
  });

  const goContact = () => navigate('/contact');

  return (
    <Box sx={{ background: '#000', color: '#fff', minHeight: '100vh' }}>
      <Breadcrumbs items={BREADCRUMBS} />
      {/* Hero */}
      <Box sx={{
        position: 'relative',
        pt: { xs: 12, md: 18 },
        pb: { xs: 8, md: 12 },
        px: { xs: 3, md: 6 },
        overflow: 'hidden',
      }}>
        {/* Ambient radial glow */}
        <Box sx={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(ellipse at 50% 20%, rgba(255,255,255,0.06) 0%, rgba(0,0,0,0) 55%)',
          pointerEvents: 'none',
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 500, fontSize: '0.68rem',
              letterSpacing: '0.24em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.4)',
              textAlign: 'center', mb: 3,
            }}>
              Languages · 32 Supported
            </Typography>

            <Typography component="h1" sx={{
              fontFamily: 'Inter', fontWeight: 900,
              fontSize: 'clamp(2.5rem, 7vw, 6rem)',
              color: '#fff', lineHeight: 0.98, letterSpacing: '-0.05em',
              textAlign: 'center', mb: 3,
              maxWidth: 1000, mx: 'auto',
            }}>
              Your voice.<br />Every language.
            </Typography>

            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 300,
              fontSize: 'clamp(1rem, 1.7vw, 1.25rem)',
              color: 'rgba(255,255,255,0.55)',
              lineHeight: 1.55, textAlign: 'center',
              maxWidth: 640, mx: 'auto', mb: 5,
            }}>
              From English and Chinese to Hindi, Spanish, and Arabic — dubbed with voice cloning,
              lip-sync, and language-specific timing calibration.
            </Typography>

            {/* Chips */}
            <Box sx={{
              display: 'flex', flexWrap: 'wrap',
              justifyContent: 'center', gap: 1.2, mb: 6,
            }}>
              {['32 Languages', 'Voice Cloning', 'Perfect Lip-Sync', 'Sub-4-min Turnaround'].map((label) => (
                <Box key={label} sx={{
                  px: 2, py: 0.9,
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '999px',
                  background: 'rgba(255,255,255,0.03)',
                  backdropFilter: 'blur(12px)',
                }}>
                  <Typography sx={{
                    fontFamily: 'Inter', fontWeight: 600, fontSize: '0.72rem',
                    letterSpacing: '0.12em', textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.75)',
                  }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={goContact}
                sx={{
                  background: '#fff',
                  color: '#000',
                  fontFamily: 'Inter', fontWeight: 700,
                  fontSize: '0.85rem', letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  px: 4, py: 1.6, borderRadius: '2px',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.88)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 32px rgba(255,255,255,0.15)',
                  },
                  transition: 'all 0.25s ease',
                }}
              >
                Book a Demo
              </Button>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Most Popular */}
      <Box sx={{
        py: { xs: 8, md: 14 },
        px: { xs: 3, md: 6 },
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Most Popular"
            title="Where your audience lives"
            subtitle="Hand-picked focus languages with the highest demand for premium dubbing quality."
          />

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            gap: { xs: 2, md: 2.5 },
          }}>
            {FEATURED_LANGUAGES.map((lang, i) => (
              <FeaturedCard key={lang.name} lang={lang} index={i} />
            ))}
          </Box>
        </Container>
      </Box>

      {/* All Supported */}
      <Box sx={{
        py: { xs: 8, md: 14 },
        px: { xs: 3, md: 6 },
        borderTop: '1px solid rgba(255,255,255,0.06)',
        background: 'rgba(255,255,255,0.01)',
      }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Complete Coverage"
            title="All supported languages"
            subtitle="Every language available today across the DubLabs platform."
          />

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(5, 1fr)',
              lg: 'repeat(6, 1fr)',
            },
            gap: { xs: 1.5, md: 1.75 },
          }}>
            {ALL_LANGUAGES.map((lang, i) => (
              <LanguageChip key={lang.name} lang={lang} index={i} />
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{
        py: { xs: 10, md: 16 },
        px: { xs: 3, md: 6 },
        borderTop: '1px solid rgba(255,255,255,0.06)',
        textAlign: 'center',
      }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 500, fontSize: '0.68rem',
              letterSpacing: '0.24em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.4)', mb: 2.5,
            }}>
              Ready to go global?
            </Typography>
            <Typography component="h2" sx={{
              fontFamily: 'Inter', fontWeight: 900,
              fontSize: 'clamp(2rem, 5vw, 4rem)',
              color: '#fff', lineHeight: 1.02,
              letterSpacing: '-0.045em', mb: 3,
            }}>
              Dub your video today.<br />See the quality yourself.
            </Typography>
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 300,
              fontSize: 'clamp(0.95rem, 1.5vw, 1.15rem)',
              color: 'rgba(255,255,255,0.5)',
              lineHeight: 1.55, mb: 5,
              maxWidth: 560, mx: 'auto',
            }}>
              Book a demo and we'll dub a clip of your content in your target languages so you
              can judge quality on your own material.
            </Typography>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={goContact}
              sx={{
                background: '#fff',
                color: '#000',
                fontFamily: 'Inter', fontWeight: 700,
                fontSize: '0.85rem', letterSpacing: '0.08em',
                textTransform: 'uppercase',
                px: 4, py: 1.6, borderRadius: '2px',
                '&:hover': {
                  background: 'rgba(255,255,255,0.88)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 32px rgba(255,255,255,0.15)',
                },
                transition: 'all 0.25s ease',
              }}
            >
              Book a Demo
            </Button>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
}
